import moment from 'moment';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CalendarTimeSlotSelect, MomentService, TimeSlot, TimeSlotSelect } from '@contact/upgrade';

@Component({
  selector: 'cng-time-slot-selector',
  templateUrl: './time-slot-selector.component.html',
  styleUrls: ['./time-slot-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSlotSelectorComponent implements OnChanges {
  // parent bindings:
  @Input() availableTimeSlots: TimeSlot[];
  @Input() initialSelectedTimeSlot: TimeSlot | undefined;
  @Input() timeSlotValidatorActivated: boolean;
  @Output() timeSlotUpdated: EventEmitter<TimeSlot | undefined> = new EventEmitter();

  // template bindings
  selectedTimeSlotSelect: TimeSlotSelect | undefined;
  selectedTimeSlot: TimeSlot | undefined;
  calendarTimeSlotSelect: CalendarTimeSlotSelect;

  // internal variables:
  private availableDayTimeSlots: TimeSlotSelect[] = [];
  private displayedWeekNumber: number = 0;
  private displayedYear: number = 0;
  private minDay: moment.Moment = moment(0);
  private maxDay: moment.Moment = moment(0);

  constructor(private readonly timeUtil: MomentService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { ['availableTimeSlots']: timeSlots } = changes;
    if (!timeSlots || !timeSlots.currentValue || (!timeSlots.isFirstChange() && timeSlots.previousValue === timeSlots.currentValue)) {
      return;
    }
    if (!!this.availableTimeSlots && this.availableTimeSlots.length > 0) {
      this.resetTimeSlots();
    }
  }

  resetTimeSlots() {
    // reset selected data
    this.clearDayTimeSlot();
    this.clearTimeSlot();

    let localMap = {};
    this.availableDayTimeSlots = [];
    this.availableTimeSlots.forEach(timeSlot => {
      let daySt = moment(timeSlot.beginDate).format('YYYY_MM_DD');
      let mapEntry: TimeSlotSelect;
      // to find the min date
      if (this.minDay.isSame(moment(0)) || moment(this.minDay).isAfter(timeSlot.beginDate)) {
        this.minDay = moment(timeSlot.beginDate);
      }
      // to find the max date
      if (this.maxDay.isSame(moment(0)) || moment(timeSlot.beginDate).isAfter(this.maxDay)) {
        this.maxDay = moment(timeSlot.beginDate);
      }
      // select time slot group by date
      if (!localMap.hasOwnProperty(daySt)) {
        mapEntry = {
          daySt: moment(timeSlot.beginDate).format('DD/MM/YYYY'),
          timeSlots: [],
          weekNumber: moment(timeSlot.beginDate).week(),
        } as TimeSlotSelect;

        this.availableDayTimeSlots.push(mapEntry);
        localMap[daySt] = mapEntry;
      } else {
        mapEntry = localMap[daySt];
      }
      if (!!mapEntry) {
        mapEntry.timeSlots.push(timeSlot);
      }
      // to initialize the selected time slot if we have initial value
      if (
        !!this.initialSelectedTimeSlot &&
        !!mapEntry &&
        (this.initialSelectedTimeSlot.id === timeSlot.id ||
          (this.initialSelectedTimeSlot.beginDate === timeSlot.beginDate && this.initialSelectedTimeSlot.endDate === timeSlot.endDate))
      ) {
        this.resetDayTimeSlot(mapEntry);
        this.selectedTimeSlot = timeSlot;
      }
    });
    // initialize the calendar of time slot
    if (this.availableDayTimeSlots.length > 0) {
      // if we have the initial value, we display the week of it, if not we display the first week
      if (!!this.initialSelectedTimeSlot && !!this.selectedTimeSlot) {
        this.displayedWeekNumber = moment(this.initialSelectedTimeSlot.beginDate).week();
        this.displayedYear = moment(this.initialSelectedTimeSlot.beginDate).year();
      } else {
        this.displayedWeekNumber = this.minDay.week();
        this.displayedYear = this.minDay.year();
      }
      let dayTimeSlotToPrint: TimeSlotSelect[] = this.availableDayTimeSlots
        .filter(
          timeSlot =>
            timeSlot.weekNumber === this.displayedWeekNumber &&
            (moment(timeSlot.daySt, 'DD/MM/YYYY').year() === this.displayedYear || this.isBetweenEndAndBeginningOfYearWeek(timeSlot))
        )
        .sort((currDayTimeSlot: TimeSlotSelect, nextDayTimeSlot: TimeSlotSelect) =>
          moment(currDayTimeSlot.daySt, 'DD/MM/YYYY').diff(moment(nextDayTimeSlot.daySt, 'DD/MM/YYYY'))
        );
      let startOfWeek: string = dayTimeSlotToPrint[0].daySt;
      let endOfWeek: string = dayTimeSlotToPrint[dayTimeSlotToPrint.length - 1].daySt;
      this.calendarTimeSlotSelect = {
        dayTimeSlotToPrint,
        startOfWeek,
        endOfWeek,
      };
    }
    // notify the original value if we have
    if (!!this.selectedTimeSlot) {
      this.notifySelectedTimeSlot(this.selectedTimeSlot);
    }
  }

  isBetweenEndAndBeginningOfYearWeek(timeSlot: TimeSlotSelect): boolean {
    return timeSlot.weekNumber === 1 && moment(timeSlot.daySt, 'DD/MM/YYYY').month() === 11;
  }

  readableTime(date: number): string {
    return '<time>' + this.timeUtil.readableTime(date) + '</time>';
  }

  readableTimeSlotSelect(timeSlot: TimeSlot) {
    return `${this.readableTimeSlotSelectWithoutContactThreshold(timeSlot)} (${timeSlot.contactCount}/${timeSlot.contactThreshold})`;
  }

  readableTimeSlotSelectWithoutContactThreshold(timeSlot: TimeSlot) {
    return `${this.readableTime(timeSlot.beginDate)} - ${this.readableTime(timeSlot.endDate)}`;
  }

  isValidTimeSlot(timeSlot: TimeSlot): boolean {
    return this.timeSlotValidatorActivated ? moment(timeSlot.endDate).isAfter() && timeSlot.contactCount < timeSlot.contactThreshold : true;
  }

  /**
   * Called when selectedTimeSlotSelect to be changed
   * @param timeSlotSelect
   */
  resetDayTimeSlot(timeSlotSelect: TimeSlotSelect): void {
    // sort is done before displayed
    if (!!timeSlotSelect) {
      this.selectedTimeSlotSelect = timeSlotSelect;
      this.selectedTimeSlotSelect.timeSlots.sort(
        (curTimeSlot: TimeSlot, nextTimeSlot: TimeSlot) => curTimeSlot.beginDate - nextTimeSlot.beginDate
      );
    }
    this.clearTimeSlot();
  }

  /**
   * Called when selectedTimeSlot to be changed
   * @param timeSlot
   */
  notifySelectedTimeSlot(timeSlot: TimeSlot): void {
    if (!!timeSlot) {
      this.selectedTimeSlot = timeSlot;
      this.timeSlotUpdated.emit(this.selectedTimeSlot);
    }
  }

  /**
   * Called when week to be changed(calendarTimeSlotSelect to be changed)
   */
  updateCalendarTimeSlot(): void {
    let dayTimeSlotToPrint: TimeSlotSelect[] = this.availableDayTimeSlots
      .filter(
        timeSlot =>
          timeSlot.weekNumber === this.displayedWeekNumber &&
          (moment(timeSlot.daySt, 'DD/MM/YYYY').year() === this.displayedYear || this.isBetweenEndAndBeginningOfYearWeek(timeSlot))
      )
      .sort((currDayTimeSlot: TimeSlotSelect, nextDayTimeSlot: TimeSlotSelect) =>
        moment(currDayTimeSlot.daySt, 'DD/MM/YYYY').diff(moment(nextDayTimeSlot.daySt, 'DD/MM/YYYY'))
      );
    this.calendarTimeSlotSelect = {
      dayTimeSlotToPrint,
      startOfWeek: dayTimeSlotToPrint[0].daySt,
      endOfWeek: dayTimeSlotToPrint[dayTimeSlotToPrint.length - 1].daySt,
    };
  }

  canDisplayPreviousPage(): boolean {
    return (
      this.displayedYear - this.minDay.year() > 0 ||
      (this.displayedYear === this.minDay.year() && this.displayedWeekNumber - this.minDay.week() > 0)
    );
  }

  canDisplayNextPage(): boolean {
    return (
      this.displayedYear - this.maxDay.year() < 0 ||
      (this.displayedYear === this.maxDay.year() && this.displayedWeekNumber - this.maxDay.week() < 0)
    );
  }

  addNbWeek(value: number): void {
    const lastWeekNumberOfYear = moment([this.displayedYear]).isoWeeksInYear();

    this.displayedWeekNumber += value;

    const numberOfWeeksNextYear = this.displayedWeekNumber - lastWeekNumberOfYear;

    if (numberOfWeeksNextYear > 0) {
      this.displayedYear++;
      this.displayedWeekNumber = numberOfWeeksNextYear;
    }

    if (this.displayedWeekNumber <= 0) {
      this.displayedYear--;
      this.displayedWeekNumber = moment([this.displayedYear]).isoWeeksInYear() + this.displayedWeekNumber;
    }

    this.updateCalendarTimeSlot();
  }

  convertDateToIso8601(date: string): string {
    return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
  }

  clearDayTimeSlot(): void {
    this.selectedTimeSlotSelect = undefined;
  }

  clearTimeSlot(): void {
    this.selectedTimeSlot = undefined;
    this.timeSlotUpdated.emit(this.selectedTimeSlot);
  }
}
